import { CopyOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Card, DatePicker, Input, message, Select, Table, Tabs } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { utils as XLSXutils, writeFile as XLSXwriteFile } from "xlsx";
import { ContentBox } from "../../components/layout/content";
import { LoginedLayout } from "../../components/layout/loginLayout";
import { HeaderTitle } from "../../components/styled-components";
import { STATUS } from "../../constants/option";
import { UserContext } from "../../context/userContext";
import { FormatDateYMDHMS } from "../../functions/function";
import { OrderLists } from "../../res/type";
import { POST } from "../../services";
import { CHECKER_ORDERS_FILTER } from "../../services/path";
import EditModal from "../orderManangement/SearchOrder/view/editModal";
import PreviewModal from "../orderManangement/SearchOrder/view/preview_pdf";

const DashBoardPage = () => {
  const [data, setData] = useState<OrderLists[]>([]);
  const [allConfirm, setAllConfirm] = useState<number>(0);
  const [orderNo, setOrderNo] = useState<string | null>(null);
  const [filePath, setFilePath] = useState<string>("");
  const [startDateRange, setStartDateRange] = useState<string>("");
  const [endDateRange, setEndDateRange] = useState<string>("");
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().add(1, "day"));
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState<string>("0");
  const [selectedRow, setSelectedRow] = useState<OrderLists | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [isModalPreview, setIsModalPreview] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [shouldFetchAll, setShouldFetchAll] = useState(false);
  const { RangePicker } = DatePicker;
  const { user } = useContext(UserContext);
  const [searchParams, setSearchParams] = useState({
    searchTextWord: "",
    searchTextOrderNo: "",
    searchTextCategory: "",
    searchTextPlatform: "",
    searchTextPlatformStatus: "",
    searchTextLogistic: "",
    searchTextBrand: "",
    searchTextStatus: "",
    searchTextDate: "",
    searchTextBatch: "",
    searchTextInvoice: "",
    searchTextSo: "",
    searchTextTracking: "",
    searchTextTotalPrice: "",
  });

  const allStatus = [
    { status: "All", key: "0" },
    { status: "Ready", key: "1" },
    { status: "Confirm", key: "2" },
    { status: "Picked", key: "3" },
    { status: "Packed", key: "4" },
    { status: "Checked Out", key: "5" },
    { status: "Shipping", key: "12" },
    { status: "Complete", key: "6" },
    { status: "Cancel", key: "8" },
    { status: "Return", key: "9" },
    { status: "Cancel Shipping", key: "11" },
    { status: "Unoperate", key: "99" },
  ];

  useEffect(() => {
    fetchDefaultDate();
  }, []);

  useEffect(() => {
    fetchData();
  }, [status, startDateRange, endDateRange]);

  useEffect(() => {
    if (shouldFetchAll && searchParams.searchTextWord === "") {
      fetchData();
      setShouldFetchAll(false);
    }
  }, [searchParams, shouldFetchAll]);

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const exportToExcel = () => {
    const worksheet = XLSXutils.json_to_sheet(data);
    const workbook = XLSXutils.book_new();
    XLSXutils.book_append_sheet(workbook, worksheet, "Orders");
    XLSXwriteFile(workbook, "orders.xlsx");
  };

  const checkMatch = (fieldValue: string, searchValue: string) => {
    if (!searchValue) return true;
    const searchValues = searchValue.split(",,, ").map((val: string) => val.trim());
    return searchValues.some((val) => {
      return fieldValue.toLowerCase().includes(val.replace("*", "").toLowerCase());
    });
  };

  const fetchDefaultDate = () => {
    const currentDate = new Date();
    const formattedStartDate = formatDate(currentDate);
    setStartDateRange(formattedStartDate);
    const nextDayDate = new Date(currentDate);
    nextDayDate.setDate(currentDate.getDate() + 1);
    const formattedEndDate = formatDate(nextDayDate);
    setEndDateRange(formattedEndDate);
  };

  const fetchData = async () => {
    if (startDateRange === "" || endDateRange === "") return;
    try {
      setAllConfirm(0);
      setData([]);
      const requestBody = {
        search: searchParams.searchTextWord ?? "",
        status: Number(status),
        start_date: startDateRange,
        end_date: endDateRange,
        is_batch_time: false,
      };
      const response = await POST(CHECKER_ORDERS_FILTER, requestBody);
      if (response.result.length > 0) {
        message.success("ดึงข้อมูลสำเร็จ");
      } else {
        message.warning("ไม่พบข้อมูล");
      }

      setData(response.result);
      setAllConfirm(response.result.length);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleSearchAll = () => {
    setSearchParams((prev) => ({ ...prev, searchTextWord: "" }));
    setShouldFetchAll(true);
  };

  const handleTabChange = (status: string) => {
    setSearchParams((prev) => {
      const newParams = { ...prev, searchTextWord: "" };
      setTimeout(() => {
        setStatus(status);
      }, 0);
      return newParams;
    });
  };

  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: string[]) => {
    if (dates) {
      if (typeof dateStrings[0] === "string") {
        setSearchParams((prev) => ({ ...prev, searchTextWord: "" }));
        setStartDateRange(dateStrings[0]);
        setEndDateRange(dateStrings[1]);
        setStartDate(dates[0]);
        setEndDate(dates[1]);
      }
    }
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSearchInputChange = (field: string, value: string) => {
    setSearchParams({ ...searchParams, [field]: value });
    setCurrentPage(1);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleOpenModal = (record: OrderLists) => {
    setSelectedRow(record);
    setIsModalVisible(true);
  };

  const handlePreviewPdfModal = (record: OrderLists) => {
    setOrderNo(record.order_no);
    setFilePath(record.file_path ?? "");
    setIsModalPreview(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setIsModalPreview(false);
    setSelectedRow(null);
  };

  const filteredData = data.filter((item) => {
    return (
      checkMatch(item.order_no ?? "", searchParams.searchTextOrderNo) &&
      checkMatch(item.category ?? "", searchParams.searchTextCategory) &&
      checkMatch(item.platform ?? "", searchParams.searchTextPlatform) &&
      checkMatch(item.logistic ?? "", searchParams.searchTextLogistic) &&
      checkMatch(item.brand ?? "", searchParams.searchTextBrand) &&
      checkMatch(item.operation_status ?? "", searchParams.searchTextStatus) &&
      checkMatch(item.create_date ?? "", searchParams.searchTextDate) &&
      checkMatch(item.batch_id ?? "", searchParams.searchTextBatch) &&
      checkMatch(item.tracking_no ?? "", searchParams.searchTextTracking) &&
      checkMatch(item.invoice_no ?? "", searchParams.searchTextInvoice) &&
      checkMatch(item.so_no ?? "", searchParams.searchTextSo) &&
      checkMatch(item.total_price ?? "", searchParams.searchTextTotalPrice) &&
      checkMatch(item.platform_status ?? "", searchParams.searchTextPlatformStatus)
    );
  });

  const columns2 = [
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "25px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            So No:
          </span>
          <Input
            placeholder="Search So No."
            value={searchParams.searchTextSo}
            onChange={(e) => handleSearchInputChange("searchTextSo", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "so_no",
      key: "so_no",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.so_no ?? "";
        const B = b.so_no ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "25px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Invoice No:
          </span>
          <Input
            placeholder="Search Invoice No."
            value={searchParams.searchTextInvoice}
            onChange={(e) => handleSearchInputChange("searchTextInvoice", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "invoice_no",
      key: "invoice_no",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.invoice_no ?? "";
        const B = b.invoice_no ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "25px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Order No:
          </span>
          <Input
            placeholder="Search Order No."
            value={searchParams.searchTextOrderNo}
            onChange={(e) => handleSearchInputChange("searchTextOrderNo", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "order_no",
      key: "order_no",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.order_no ?? "";
        const B = b.order_no ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "25px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Tracking No:
          </span>
          <Input
            placeholder="Search Tracking No."
            value={searchParams.searchTextTracking}
            onChange={(e) => handleSearchInputChange("searchTextTracking", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "tracking_no",
      key: "tracking_no",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.tracking_no ?? "";
        const B = b.tracking_no ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "25px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Status:
          </span>
          <Select
            mode="tags"
            placeholder="Select or Input Status"
            value={selectedStatus}
            onChange={(values) => {
              setSelectedStatus(values);
              handleSearchInputChange("searchTextStatus", values.join(",,, "));
            }}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
          >
            {STATUS.map((item) => (
              <Select.Option key={item.value} value={item.label}>
                {item.label}
              </Select.Option>
            ))}
          </Select>
        </div>
      ),
      dataIndex: "operation_status",
      key: "operation_status",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.operation_status ?? "";
        const B = b.operation_status ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "25px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "110px",
              maxWidth: "110px",
            }}
          >
            Platform Status:
          </span>
          <Input
            placeholder="Search Platform Status"
            value={searchParams.searchTextPlatformStatus}
            onChange={(e) => handleSearchInputChange("searchTextPlatformStatus", e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "platform_status",
      key: "platform_status",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.platform_status ?? "";
        const B = b.platform_status ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "25px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Batch No:
          </span>
          <Input
            placeholder="Search Batch ID"
            value={searchParams.searchTextBatch}
            onChange={(e) => handleSearchInputChange("searchTextBatch", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "batch_id",
      key: "batch_id",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.batch_id ?? "";
        const B = b.batch_id ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "25px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Platform:
          </span>
          <Input
            placeholder="Search Platform"
            value={searchParams.searchTextPlatform}
            onChange={(e) => handleSearchInputChange("searchTextPlatform", e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "platform",
      key: "platform",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.platform ?? "";
        const B = b.platform ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "25px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Store:
          </span>
          <Input
            placeholder="Search Store"
            value={searchParams.searchTextBrand}
            onChange={(e) => handleSearchInputChange("searchTextBrand", e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "brand",
      key: "brand",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.brand ?? "";
        const B = b.brand ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "25px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Logistic:
          </span>
          <Input
            placeholder="Search Logistic"
            value={searchParams.searchTextLogistic}
            onChange={(e) => handleSearchInputChange("searchTextLogistic", e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "logistic",
      key: "logistic",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.logistic ?? "";
        const B = b.logistic ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "25px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Category:
          </span>
          <Input
            placeholder="Search Category"
            value={searchParams.searchTextCategory}
            onChange={(e) => handleSearchInputChange("searchTextCategory", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "category",
      key: "category",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.category ?? "";
        const B = b.category ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "25px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Date:
          </span>
          <Input
            placeholder="Search Date"
            value={searchParams.searchTextDate}
            onChange={(e) => handleSearchInputChange("searchTextDate", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "create_date",
      key: "create_date",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.create_date ?? "";
        const B = b.create_date ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>
            {FormatDateYMDHMS(text.replace("T", " ").replace("Z", ""))}
          </span>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (record: OrderLists) =>
        [1, 5, 6, 7].includes(user.role_id) ? (
          <Button type="primary" onClick={() => handlePreviewPdfModal(record)}>
            Preview
          </Button>
        ) : null,
    },
  ];

  return (
    <LoginedLayout>
      <ContentBox>
        <HeaderTitle>
          <h2>Order Summary</h2>
        </HeaderTitle>
        <div
          style={{
            backgroundColor: "#ffffff",
            padding: "20px",
            borderRadius: "7px",
          }}
        >
          <Tabs onChange={handleTabChange} type="card">
            {allStatus.map((statusAll) => (
              <Tabs.TabPane tab={statusAll.status} key={statusAll.key}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <RangePicker
                      value={[startDate, endDate]}
                      onChange={handleRangeChange}
                      format="YYYY-MM-DD"
                      allowClear
                    />
                  </div>
                  <Button type="primary" onClick={exportToExcel}>
                    Export Excel
                  </Button>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    margin: "20px",
                  }}
                >
                  {statusAll.status !== "All" ? (
                    <Card
                      title={"จำนวน " + statusAll.status + " ทั้งหมด"}
                      bordered={false}
                      style={{
                        width: 300,
                        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <h2 style={{ display: "flex", justifyContent: "center" }}>
                        {allConfirm} Order
                      </h2>
                    </Card>
                  ) : null}
                </div>
                <div style={{ width: "100%", overflowX: "auto" }}>
                  {statusAll.status === "All" ? (
                    <>
                      <div
                        style={{
                          justifyContent: "space-between",
                          display: "flex",
                          gap: "20px",
                          marginBottom: "20px",
                        }}
                      >
                        <Input.Search
                          placeholder="Search for Order No., Tracking No., Batch No."
                          value={searchParams.searchTextWord}
                          onSearch={fetchData}
                          onChange={(e) =>
                            handleSearchInputChange("searchTextWord", e.target.value)
                          }
                          style={{ width: "80%" }}
                        />
                        <Button
                          style={{ width: "20%", backgroundColor: "#e28743" }}
                          type="primary"
                          onClick={() => handleSearchAll()}
                        >
                          ดูทั้งหมด
                        </Button>
                      </div>
                      <Table
                        columns={columns2}
                        dataSource={filteredData}
                        scroll={{ x: 1500 }} // Adjust the value based on your needs
                        pagination={{
                          position: ["bottomRight"],
                          pageSize: pageSize,
                          showSizeChanger: true,
                          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                          onShowSizeChange: (current, size) => handlePageSizeChange(size),
                          current: currentPage,
                          onChange: (page) => setCurrentPage(page),
                          total: filteredData.length,
                        }}
                        onRow={(record) => ({
                          onDoubleClick: () => handleOpenModal(record),
                        })}
                      />
                    </>
                  ) : (
                    <Table
                      columns={columns2}
                      dataSource={filteredData}
                      scroll={{ x: 1500 }} // Adjust the value based on your needs
                      pagination={{
                        position: ["bottomRight"],
                        pageSize: pageSize,
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        onShowSizeChange: (current, size) => handlePageSizeChange(size),
                        current: currentPage,
                        onChange: (page) => setCurrentPage(page),
                        total: filteredData.length,
                      }}
                      onRow={(record) => ({
                        onDoubleClick: () => handleOpenModal(record),
                      })}
                    />
                  )}
                </div>
              </Tabs.TabPane>
            ))}
          </Tabs>
          {selectedRow && (
            <EditModal
              visible={isModalVisible}
              onCancel={handleCloseModal}
              order_no={selectedRow?.order_no || ""}
              tracking_no={selectedRow?.tracking_no || ""}
              no={selectedRow?.no || 0}
            />
          )}
          {isModalPreview && orderNo && (
            <PreviewModal
              visible={isModalPreview}
              onCancel={handleCloseModal}
              order_no={orderNo} // Pass the selected order as a prop
              file_path={filePath}
            />
          )}
        </div>
      </ContentBox>
    </LoginedLayout>
  );
};

export default DashBoardPage;
